import React, { useCallback, useEffect, useState } from "react";
import { Building2, ArrowRight, Database, AlertTriangle, MessageSquareWarning, BookOpenCheck, Plus, Archive, Trash2, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { fetchFirmOverview, archiveClient, unarchiveClient, deleteClient } from "../../api";
import { AddClientModal } from "./AddClientModal";

const money = (n) =>
  `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const STATE_COLORS = {
  AWAITING_DOCUMENTS: "bg-white/5 text-zinc-400 border-white/10",
  PAYMENT_PENDING: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  PROCESSING: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  OPEN_ITEMS: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  IN_REVIEW: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30",
  READY_TO_ISSUE: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  ISSUED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  REOPENED: "bg-rose-500/10 text-rose-400 border-rose-500/30",
};

const Stat = ({ label, value, warn }) => (
  <div className="bg-white/[0.04] border border-white/10 rounded-md px-2.5 py-2">
    <div className="text-[10px] text-zinc-400">{label}</div>
    <div className={`text-sm font-black font-mono ${warn && value > 0 ? "text-amber-400" : "text-white"}`}>{value}</div>
  </div>
);

const ConfirmDialog = ({ pending, busy, onCancel, onConfirm }) => {
  if (!pending) return null;
  const isDelete = pending.action === "delete";
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={isDelete || busy ? undefined : onCancel} data-testid="confirm-dialog">
      <div className="glass-panel rounded-md shadow-2xl max-w-sm w-full p-6 border border-white/10 space-y-4 bg-zinc-900/95" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center space-x-2">
          {isDelete ? <Trash2 className="w-5 h-5 text-rose-400" /> : <Archive className="w-5 h-5 text-amber-400" />}
          <h3 className="font-bold text-sm text-white">
            {isDelete ? "Permanently delete" : "Archive"} {pending.name}?
          </h3>
        </div>
        <p className="text-xs text-zinc-400 leading-relaxed">
          {isDelete
            ? "This permanently removes the engagement, its transactions and its persisted operation history. This cannot be undone."
            : "The engagement is hidden from the entity switcher and firm grid. All state is kept — you can restore it anytime."}
        </p>
        <div className="flex items-center space-x-2">
          <button data-testid="confirm-cancel-btn" onClick={onCancel} disabled={busy} className="flex-1 px-3 py-2 bg-zinc-900 hover:bg-white/5 border border-white/10 text-zinc-300 text-xs font-semibold rounded-md transition disabled:opacity-50">
            Cancel
          </button>
          <button
            data-testid="confirm-action-btn"
            onClick={onConfirm}
            disabled={busy}
            className={`flex-1 px-3 py-2 text-white text-xs font-semibold rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed ${isDelete ? "bg-rose-600 hover:bg-rose-500" : "bg-amber-600 hover:bg-amber-500"}`}
          >
            {busy ? "Working…" : isDelete ? "Delete Permanently" : "Archive Client"}
          </button>
        </div>
      </div>
    </div>
  );
};

const FirmOverviewView = ({ onOpenEntity, coaOptions, onClientCreated, onClientsChanged }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetchFirmOverview().then(setData).catch(() => setError(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async () => {
    const { action, slug, name } = pending;
    setBusy(true);
    try {
      if (action === "delete") {
        await deleteClient(slug);
        toast.success(`${name} deleted permanently`);
      } else {
        await archiveClient(slug);
        toast.success(`${name} archived`, { description: "State kept — restore anytime from the archived list." });
      }
      load();
      onClientsChanged(slug);
    } catch (e) {
      toast.error(`${action === "delete" ? "Delete" : "Archive"} failed`, {
        description: (e.response && e.response.data && e.response.data.detail) || "Request failed",
      });
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  const restore = async (slug, name) => {
    try {
      await unarchiveClient(slug);
      toast.success(`${name} restored`);
      load();
      onClientsChanged(slug);
    } catch (e) {
      toast.error("Restore failed");
    }
  };

  const activeRows = data ? data.entities.filter((e) => !e.archived) : [];
  const archivedRows = data ? data.entities.filter((e) => e.archived) : [];

  if (error)
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-rose-400" data-testid="view-firm">
        Failed to load the firm overview.
      </div>
    );
  if (!data)
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-zinc-400 animate-pulse" data-testid="view-firm">
        Loading all engagements…
      </div>
    );

  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar p-8" data-testid="view-firm">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="border-b border-white/10 pb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white">Firm Overview</h1>
            <p className="text-zinc-400 text-xs mt-1">
              Every client engagement, side by side — status, open items and anomalies at a glance.
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              data-testid="add-client-btn"
              onClick={() => setAddOpen(true)}
              className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-md transition shadow-glow-blue flex items-center space-x-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Onboard Client</span>
            </button>
            <span
              data-testid="persistence-badge"
              className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold flex items-center"
            >
              <Database className="w-3.5 h-3.5 mr-1.5" />
              {data.persistence.ops_persisted} ops persisted · survives restarts
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="firm-entity-grid">
          {activeRows.map((e) => (
            <div
              key={e.slug}
              data-testid={`firm-card-${e.slug}`}
              className="glass-card rounded-md border border-white/10 p-5 space-y-4 hover:border-blue-500/40 hover:border-white/20 transition"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-2.5">
                  <div className="w-9 h-9 rounded-md bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400 flex-shrink-0">
                    <Building2 className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-bold text-sm text-white leading-tight">{e.name}</div>
                    <div className="text-[10px] text-zinc-400 font-mono">Form {e.form} · {e.statements} statements</div>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    data-testid={`archive-client-${e.slug}`}
                    title="Archive client"
                    onClick={() => setPending({ action: "archive", slug: e.slug, name: e.name })}
                    className="p-1.5 text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 rounded-md transition"
                  >
                    <Archive className="w-3.5 h-3.5" />
                  </button>
                  {e.custom && (
                    <button
                      data-testid={`delete-client-${e.slug}`}
                      title="Delete client permanently"
                      onClick={() => setPending({ action: "delete", slug: e.slug, name: e.name })}
                      className="p-1.5 text-zinc-500 hover:text-rose-400 hover:bg-rose-500/10 rounded-md transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${STATE_COLORS[e.state] || "bg-white/5 text-zinc-400 border-white/10"}`}>
                  {e.state.replace(/_/g, " ")}
                </span>
                <span className="text-[10px] text-zinc-400">Client sees: {e.client_step}</span>
              </div>

              <div className={`rounded-md px-2.5 py-1.5 text-[11px] border ${e.open_blocking > 0 ? "bg-rose-500/10 border-rose-500/30 text-rose-400" : "bg-white/[0.04] border-white/10 text-zinc-400"}`}>
                {e.ball_in_court}
              </div>

              <div className="grid grid-cols-3 gap-2" data-testid={`firm-stats-${e.slug}`}>
                <Stat label="Line items" value={e.total_items} />
                <Stat label="Needs review" value={e.needs_review} warn />
                <Stat label="Blocking" value={e.open_blocking} warn />
                <Stat label="Anomalies" value={e.anomaly_warnings} warn />
                <Stat label="Open notes" value={e.open_notes} warn />
                <Stat label="AJEs pending" value={e.pending_ajes} warn />
              </div>

              <div className="flex justify-between items-center text-[11px] pt-2 border-t border-white/5">
                <div>
                  <span className="text-zinc-400">Net income: </span>
                  <span className={`font-bold font-mono ${e.net_income < 0 ? "text-rose-400" : "text-emerald-400"}`}>
                    {money(e.net_income)}
                  </span>
                </div>
                <div>
                  <span className="text-zinc-400">Revenue: </span>
                  <span className="font-bold font-mono text-zinc-200">{money(e.net_revenue)}</span>
                </div>
              </div>

              <button
                data-testid={`firm-open-${e.slug}`}
                onClick={() => onOpenEntity(e.slug)}
                className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-md transition flex items-center justify-center space-x-1.5"
              >
                <span>Open Workspace</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        {/* ARCHIVED CLIENTS */}
        {archivedRows.length > 0 && (
          <div className="glass-card rounded-md border border-white/10 p-5 space-y-3" data-testid="archived-clients-card">
            <h3 className="text-sm font-bold text-zinc-400 flex items-center border-b border-white/10 pb-3">
              <Archive className="w-4 h-4 mr-2" /> Archived Clients ({archivedRows.length})
            </h3>
            <div className="space-y-2">
              {archivedRows.map((e) => (
                <div key={e.slug} className="flex items-center justify-between bg-white/[0.04] border border-white/10 rounded-md px-4 py-2.5 text-xs" data-testid={`archived-row-${e.slug}`}>
                  <div>
                    <span className="font-bold text-zinc-300">{e.name}</span>
                    <span className="text-zinc-500 ml-2 font-mono">Form {e.form} · {e.state.replace(/_/g, " ")} · {e.total_items} items</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      data-testid={`restore-client-${e.slug}`}
                      onClick={() => restore(e.slug, e.name)}
                      className="px-3 py-1.5 bg-zinc-900 hover:bg-white/5 border border-white/10 text-zinc-300 text-[11px] font-semibold rounded-md transition flex items-center space-x-1"
                    >
                      <ArchiveRestore className="w-3 h-3 text-emerald-400" />
                      <span>Restore</span>
                    </button>
                    {e.custom && (
                      <button
                        data-testid={`delete-client-${e.slug}`}
                        onClick={() => setPending({ action: "delete", slug: e.slug, name: e.name })}
                        className="px-3 py-1.5 bg-zinc-900 hover:bg-rose-500/10 border border-white/10 text-rose-400 text-[11px] font-semibold rounded-md transition flex items-center space-x-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        <span>Delete</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
          <div className="glass-card rounded-md border border-white/10 p-4 flex items-center space-x-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <p className="text-zinc-400 leading-relaxed">
              <strong className="text-zinc-200">{data.entities.reduce((a, e) => a + e.anomaly_warnings, 0)} anomaly warnings</strong> firm-wide — spend spikes and rate drift surfaced before review.
            </p>
          </div>
          <div className="glass-card rounded-md border border-white/10 p-4 flex items-center space-x-3">
            <MessageSquareWarning className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <p className="text-zinc-400 leading-relaxed">
              <strong className="text-zinc-200">{data.entities.reduce((a, e) => a + e.needs_review, 0)} items in review</strong> across all engagements awaiting a human decision.
            </p>
          </div>
          <div className="glass-card rounded-md border border-white/10 p-4 flex items-center space-x-3">
            <BookOpenCheck className="w-5 h-5 text-emerald-500 flex-shrink-0" />
            <p className="text-zinc-400 leading-relaxed">
              Every action is persisted to MongoDB as a <strong className="text-zinc-200">deterministic operation log</strong> and replayed identically on restart.
            </p>
          </div>
        </div>
      </div>

      <AddClientModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        coaOptions={coaOptions || []}
        onCreated={(slug) => {
          load();
          onClientCreated(slug);
        }}
      />
      <ConfirmDialog pending={pending} busy={busy} onCancel={() => setPending(null)} onConfirm={runAction} />
    </div>
  );
};

export default FirmOverviewView;
